import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames/bind';
import _isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';

// Components
import CheckLink from '../CheckLink';
import Heading from '../Heading';
import Paper from '../Paper';
import Modal from '../Modal';
import Button from '../Button';
import NavBar from './NavBar';
import CheckboxField from '../CheckboxField';

// Icons
import FilterIcon from '../../icons/filter.svg';
import RefreshIcon from '../../icons/refresh.svg';

// State
import { fetchPizzaFilterIsNeeded } from '../../state/modules/catalog/actions';

// Styles
import styles from './PizzaFilter.styl';

const cx = classNames.bind(styles);

export default function PizzaFilter(props) {
  const { goods, changeFilterPizza, className, name } = props;

  const dispatch = useDispatch();
  const pizzaFilter = useSelector(state => state.catalog.pizzaFilter);
  const isPhone = useSelector(state => state.responsive.isPhone);

  const [filterId, setFilterId] = useState(null);
  // выбранные ингредиенты для фильтрации
  const [ingredientsIds, setIngredientsIds] = useState([]);
  // после выбора игредиента смотрим, какие остались доступными
  const [availableIngredientsIds, setAvailableIngredientsIds] = useState({});
  const [pizzaFilterIsOpen, setPizzaFilterIsOpen] = useState(false);

  // для телефона
  // выбранная категория (сыр/мясо/овощи)
  const [activeGroupType, setActiveGroupType] = useState('');
  // количество доступных пицц после фильтрации (показать 24 пиццы)
  const [pizzaCount, setPizzaCount] = useState(0);
  const [pizzaFilterPhoneIsOpen, setPizzaFilterPhoneIsOpen] = useState(false);

  const types = [];

  // извлекаем все типы из пицц
  goods.forEach(good => {
    if (good.types) {
      good.types.forEach(type => {
        types.push(type.name);
      });
    }
  });

  // избавляемся от дублей
  const filtredExpressButtons = [...new Set(types)];

  const changeFilter = () => {
    // фильтруем пиццы по ингредиентам и типам
    const filteredGoods = goods.filter(good => {
      // если хоть в какой-то из вариаций пиццы имеется каждый выбранный игредиент
      const isIngredientsChecked = (good.variations || []).some(variation =>
        ingredientsIds.every(ingredientsId =>
          (variation.include_ingredients || [])
            .map(includeIngredient => Number(includeIngredient.id))
            .includes(ingredientsId),
        ),
      );

      // если выбранная пицца по типу подходит под экспресс-фильтр
      const isExpressFilterChecked = (good.types || []).some(type => type.name === filterId);

      if (
        (ingredientsIds.length === 0 || isIngredientsChecked) &&
        (!filterId || isExpressFilterChecked)
      ) {
        return true;
      }

      return false;
    });

    // Находим уникальные id ингридиентов которые остались после фильтрации товаров
    const newAvailableIngredientsIds = {};

    filteredGoods.forEach(good => {
      (good.variations || []).forEach(variation => {
        (variation.include_ingredients || []).forEach(includeIngredient => {
          newAvailableIngredientsIds[includeIngredient.id] = true;
        });
      });
    });

    setAvailableIngredientsIds(newAvailableIngredientsIds);
    setPizzaCount(filteredGoods.length);
    changeFilterPizza(filteredGoods.map(filteredGood => filteredGood.id));
  };

  // выбираем фильтр на экспресс кнопку
  const onSelect = itemId => {
    const newFilterId = filterId === itemId ? null : itemId;

    setFilterId(newFilterId);
    setIngredientsIds([]);
  };

  // сбрасываем фильтры
  const refresh = () => {
    setFilterId(null);
    setIngredientsIds([]);
  };

  // выбираем ингредиент
  const onSelectIngredients = ingredientId => {
    if (!availableIngredientsIds[ingredientId]) {
      return;
    }

    // если уже содержится, то удаляем, а если нет, то добавляем
    const newIngredientsIds = ingredientsIds.includes(ingredientId)
      ? ingredientsIds.filter(id => ingredientId !== id)
      : [...ingredientsIds, ingredientId];

    setIngredientsIds(newIngredientsIds);
  };

  // открываем меню ингредиентов
  const pizzaFilterToggle = () => {
    if (isPhone) {
      // открываем боковое меню
      setPizzaFilterPhoneIsOpen(prev => !prev);

      // если мы только открыли, то устанавливаем первую категорию активной
      if (!pizzaFilterPhoneIsOpen) {
        const firstGroupType = Object.keys(pizzaFilter.activeIngredients)[0] || '';

        setActiveGroupType(firstGroupType);
      }

      return true;
    }

    return setPizzaFilterIsOpen(prev => !prev);
  };

  useEffect(() => {
    dispatch(fetchPizzaFilterIsNeeded());
  }, []);

  useEffect(() => {
    changeFilter();
  }, [filterId, ingredientsIds]);

  return (
    <div className={cx('PizzaFilter', className)}>
      <div className={cx('PizzaFilter__actions')}>
        {/* кнопка сброса */}
        <Heading className={cx('PizzaFilter__heading')} level={4}>
          {name}
        </Heading>
        <button
          type="button"
          className={cx('PizzaFilter__action', 'PizzaFilter__action_refresh', {
            PizzaFilter__action_disabled: ingredientsIds.length === 0 && !filterId,
          })}
          onClick={refresh}
        >
          <RefreshIcon width={24} height={24} />
        </button>
        {/* кнопка меню ингредиентов */}
        {!_isEmpty(pizzaFilter.activeIngredients) && (
          <button
            type="button"
            className={cx('PizzaFilter__action', 'PizzaFilter__action_toggle', {
              PizzaFilter__action_active: ingredientsIds.length > 0,
            })}
            onClick={pizzaFilterToggle}
          >
            <FilterIcon width={17} height={16} />
          </button>
        )}
      </div>
      {pizzaFilterIsOpen && (
        <>
          {/* кнопки быстрого выбора */}
          <div className={cx('PizzaFilter__express')}>
            {filtredExpressButtons.map(value => (
              <button
                key={value}
                onClick={() => onSelect(value)}
                type="button"
                className={cx('PizzaFilter__express-button', {
                  'PizzaFilter__express-button_selected': filterId === value,
                })}
              >
                {value}
              </button>
            ))}
          </div>
          <Paper className={cx('PizzaFilter__panel')}>
            <div className={cx('PizzaFilter__list')}>
              {Object.keys(pizzaFilter.activeIngredients).map(type => {
                return (
                  <div key={type} className={cx('PizzaFilter__group')}>
                    <Heading className={cx('PizzaFilter__group-title')} level={4} tagName="div">
                      {type}
                    </Heading>
                    <div className={cx('PizzaFilter__group-list')}>
                      {pizzaFilter.activeIngredients[type].map(ingredient => (
                        <CheckLink
                          key={ingredient.id}
                          className={cx('PizzaFilter__link')}
                          removed={!availableIngredientsIds[ingredient.id]}
                          onClick={() => onSelectIngredients(ingredient.id)}
                          selected={ingredientsIds.includes(ingredient.id)}
                        >
                          {ingredient.name}
                        </CheckLink>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Paper>
        </>
      )}

      {/* для мобильной версии */}
      <Modal isOpen={pizzaFilterPhoneIsOpen} onClose={pizzaFilterToggle} direction="left">
        <div className={cx('PizzaFilter__ModalBody')}>
          <div className={cx('PizzaFilter__ModalNavbar')}>
            <FormattedMessage id="modal.pizzaFilter.title">
              {title => <NavBar backText={title} onBack={pizzaFilterToggle} />}
            </FormattedMessage>
          </div>
          <div className={cx('PizzaFilter__ModalTabs')}>
            <div className={cx('PizzaFilter__ModalTabsSwipe')}>
              {Object.keys(pizzaFilter.activeIngredients).map(type => (
                <button
                  key={type}
                  type="button"
                  className={cx('PizzaFilter__ModalLink', {
                    PizzaFilter__ModalLink_active: activeGroupType === type,
                  })}
                  onClick={() => setActiveGroupType(type)}
                >
                  <span>{type}</span>
                </button>
              ))}
            </div>
          </div>
          <div className={cx('PizzaFilter__ModalContainer')}>
            <div className={cx('PizzaFilter__ModalCheckboxGroups')}>
              {Object.keys(pizzaFilter.activeIngredients).map(type => (
                <div
                  key={type}
                  className={cx('PizzaFilter__ModalCheckboxList', {
                    PizzaFilter__ModalCheckboxList_active: activeGroupType === type,
                  })}
                >
                  {pizzaFilter.activeIngredients[type].map(ingredient => (
                    <CheckboxField
                      key={ingredient.id}
                      className={cx('PizzaFilter__ModalCheckboxItem')}
                      onClick={() => onSelectIngredients(ingredient.id)}
                      value={ingredientsIds.includes(ingredient.id)}
                      label={ingredient.name}
                      disabled={!availableIngredientsIds[ingredient.id]}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className={cx('PizzaFilter__ModalFooter')}>
            <Button onClick={pizzaFilterToggle}>
              <FormattedMessage id="modal.pizzaFilter.showCount" values={{ count: pizzaCount }} />
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

PizzaFilter.defaultProps = {
  className: '',
};

PizzaFilter.propTypes = {
  className: PropTypes.string,
  changeFilterPizza: PropTypes.func.isRequired,
  goods: PropTypes.array.isRequired,
  name: PropTypes.string.isRequired,
};
